import { PLATFORM_SLUG } from '@app/shared/brand'
import type { SegnaviaPage } from '@segnavia/frame'
import { type LocalFonts, packLocalFaces } from '@segnavia/frame/local-fonts'
import { useMutation } from '@tanstack/react-query'
import { Code, Type } from 'lucide-react'
import { createElement, useEffect, useMemo, useRef, useState } from 'react'
import { useCompanionViews, useOpenCompanion } from '../../lib/companion'
import type { CompanionView } from '../Companion'
import { DocSlot } from '../DocSlot'
import { htmlPageSheetGeometry } from '../html-page-sheet'
import { Notice } from '../index'
import { PageActionFor } from '../PageAction'
import { DocFacts } from './DocFacts'
import type {
  DocFormatId,
  DocHostClient,
  DocRef,
  LoadedDocView,
  PagePreview,
} from './DocView'
import {
  askForLocalFonts,
  pageFontSource,
  readGrantedPageLocalFonts,
  useLocalFontAccess,
} from './local-page-fonts'
import { PageFonts, PageFontsNotice } from './PageFonts'
import './html-doc-view.css'

type HtmlDoc = Extract<LoadedDocView, { format: 'html' }>

const FONTS_VIEW = 'fonts'

export function htmlPagePreviewKind(
  hasPagePreview: boolean,
  kind: HtmlDoc['extra']['kind'],
): 'page' | 'iframe' {
  return hasPagePreview && kind !== 'scripted' ? 'page' : 'iframe'
}

export function htmlPageFrameSandbox(kind: HtmlDoc['extra']['kind']): string {
  return kind === 'scripted' ? 'allow-scripts' : 'allow-same-origin'
}

export function scriptedPageHeight(
  event: { data: unknown; source: unknown },
  frameWindow: unknown,
): number | null {
  if (event.source !== frameWindow) return null
  if (!event.data || typeof event.data !== 'object') return null
  const type = Reflect.get(event.data, 'type')
  const reportedHeight = Reflect.get(event.data, 'height')
  if (
    type !== `${PLATFORM_SLUG}:html-page-height` ||
    typeof reportedHeight !== 'number' ||
    !Number.isFinite(reportedHeight) ||
    reportedHeight <= 0
  )
    return null
  return reportedHeight
}

/**
 * A scripted page says so among its facts, beside Live data, because the two
 * answer one question: where what the page shows comes from. It is stated in
 * the reader's chrome, never inside the frame the page's own code controls.
 */
export function htmlPageFacts(
  facts: HtmlDoc['facts'],
  scripted: boolean,
): HtmlDoc['facts'] {
  if (!scripted) return facts
  const code = { label: 'Code', icon: Code, value: 'Runs its own' }
  const after = facts.findIndex((fact) => fact.label === 'Live data')
  return after === -1
    ? [...facts, code]
    : [...facts.slice(0, after + 1), code, ...facts.slice(after + 1)]
}

function saveDownload(download: { blob: Blob; filename: string }): void {
  const href = URL.createObjectURL(download.blob)
  const link = document.createElement('a')
  link.href = href
  link.download = download.filename
  link.click()
  URL.revokeObjectURL(href)
}

/**
 * A page prints with the reader's faces only through a host the server prints
 * that way; every other host's download prints the fallbacks the page declares.
 */
export function downloadHtmlPage(
  client: DocHostClient,
  doc: DocRef,
  variant: string | undefined,
  localFonts: LocalFonts,
  references: PagePreview['page']['fonts'],
  title: string,
): Promise<{ blob: Blob; filename: string }> {
  if (!client.download) throw new Error('This page cannot be downloaded.')
  if (localFonts.faces.length > 0 && client.printWithLocalFaces) {
    return client.printWithLocalFaces(
      doc,
      packLocalFaces(localFonts.faces, references ?? []),
      `${title}.pdf`,
    )
  }
  return client.download(doc, variant)
}

function DownloadActions({
  doc,
  client,
  downloads,
  pagePreview,
  localFonts,
  title,
}: {
  doc: DocRef
  client: DocHostClient
  downloads: HtmlDoc['downloads']
  pagePreview: PagePreview | null
  localFonts: LocalFonts
  title: string
}) {
  const download = useMutation({
    mutationFn: (variant?: string) =>
      downloadHtmlPage(
        client,
        doc,
        variant,
        localFonts,
        pagePreview?.page.fonts ?? [],
        title,
      ),
    onSuccess: saveDownload,
  })
  if (!client.download) return null

  const choices = downloads?.length
    ? downloads
    : [{ variant: '', label: 'Download PDF' }]
  return (
    <>
      {choices.map((choice) => (
        <PageActionFor
          key={choice.variant}
          type="download"
          label={download.isPending ? 'Preparing PDF…' : choice.label}
          onSelect={() => {
            if (!download.isPending)
              download.mutate(choice.variant || undefined)
          }}
        />
      ))}
      {download.error ? (
        <Notice tone="error">{download.error.message}</Notice>
      ) : null}
    </>
  )
}

/** The complete presentation shared by every hosted HTML page. */
export function HtmlDocView({
  doc,
  client,
  loaded,
  factsOnDemand,
}: {
  doc: DocRef
  client: DocHostClient
  loaded: Extract<LoadedDocView, { format: Extract<DocFormatId, 'html'> }>
  factsOnDemand: boolean
}) {
  const frame = useRef<HTMLIFrameElement>(null)
  const pageElement = useRef<SegnaviaPage>(null)
  const heightObserver = useRef<ResizeObserver | null>(null)
  const scrollBox = useRef<HTMLDivElement>(null)
  const [frameScale, setFrameScale] = useState(1)
  const [ticket, setTicket] = useState<{ url: string } | null>(null)
  const [pagePreview, setPagePreview] = useState<PagePreview | null>(null)
  const [pageElementReady, setPageElementReady] = useState(false)
  const [ticketError, setTicketError] = useState<Error | null>(null)
  const [height, setHeight] = useState<number | null>(null)
  const [unavailable, setUnavailable] = useState<string[]>([])
  const [legacy, setLegacy] = useState(false)
  const [localFonts, setLocalFonts] = useState<LocalFonts>({
    faces: [],
    unavailable: [],
  })
  const fontAccess = useLocalFontAccess()
  const [fontsNoticeShut, setFontsNoticeShut] = useState(false)
  const openCompanion = useOpenCompanion()
  const scripted = loaded.extra.kind === 'scripted'
  const { host, key, spaceId } = doc
  const previewKind = htmlPagePreviewKind(
    !!client.previewPage,
    loaded.extra.kind,
  )

  useEffect(() => {
    let current = true
    setPageElementReady(false)
    if (previewKind === 'page') {
      void import('@segnavia/frame').then(
        () => {
          if (current) setPageElementReady(true)
        },
        (error: unknown) => {
          if (current)
            setTicketError(
              error instanceof Error ? error : new Error(String(error)),
            )
        },
      )
    }
    return () => {
      current = false
    }
  }, [previewKind])

  /* The preview is drawn at the page's paper, so a new paper or orientation
     asks for a new one. */
  const sheetKey = JSON.stringify([loaded.paper, loaded.orientation])

  useEffect(() => {
    if (!sheetKey) return
    let current = true
    setTicket(null)
    setPagePreview(null)
    setTicketError(null)
    setHeight(null)
    setUnavailable([])
    setLegacy(false)
    setFontsNoticeShut(false)
    const preview =
      previewKind === 'page'
        ? (client.previewPage as NonNullable<DocHostClient['previewPage']>)({
            host,
            key,
            spaceId,
          })
        : client.previewTicket({ host, key, spaceId })
    void preview.then(
      (next) => {
        if (!current) return
        if ('page' in next) {
          setPagePreview(next)
          setLegacy(next.legacy)
          setUnavailable(next.unavailable)
        } else {
          setTicket(next)
        }
      },
      (error: unknown) =>
        current &&
        setTicketError(
          error instanceof Error ? error : new Error(String(error)),
        ),
    )
    return () => {
      current = false
    }
  }, [client, host, key, previewKind, sheetKey, spaceId])

  useEffect(() => {
    const abort = new AbortController()
    setLocalFonts({ faces: [], unavailable: [] })
    if (pagePreview && fontAccess !== null) {
      void readGrantedPageLocalFonts(
        pagePreview.page.fonts ?? [],
        fontAccess,
        abort.signal,
      ).then(
        (fonts) => setLocalFonts(fonts),
        (error: unknown) => {
          if (!(error instanceof Error && error.name === 'AbortError')) {
            setTicketError(
              error instanceof Error ? error : new Error(String(error)),
            )
          }
        },
      )
    }
    return () => abort.abort()
  }, [pagePreview, fontAccess])

  useEffect(() => {
    if (!pageElementReady) return
    const element = pageElement.current
    if (!element || !pagePreview) return
    const report = (event: Event) => {
      const error = event instanceof ErrorEvent ? event.error : null
      setTicketError(
        error instanceof Error
          ? error
          : new Error(
              event instanceof ErrorEvent && event.message
                ? event.message
                : 'The page preview could not be shown.',
            ),
      )
    }
    element.addEventListener('error', report)
    element.cspExtra = 'img-src data:; font-src data:'
    element.fonts = { source: pageFontSource, local: localFonts.faces }
    element.page = pagePreview.page
    return () => {
      element.removeEventListener('error', report)
      element.page = null
      element.fonts = undefined
    }
  }, [localFonts.faces, pageElementReady, pagePreview])

  useEffect(() => () => heightObserver.current?.disconnect(), [])

  useEffect(() => {
    if (!scripted) return
    const receiveHeight = (event: MessageEvent<unknown>) => {
      const height = scriptedPageHeight(event, frame.current?.contentWindow)
      if (height !== null) setHeight(height)
    }
    window.addEventListener('message', receiveHeight)
    return () => window.removeEventListener('message', receiveHeight)
  }, [scripted])

  /* A framed sheet fits the box's content measure, never wider. */
  const framedSheetMm = ticket
    ? htmlPageSheetGeometry(loaded.paper, loaded.orientation ?? 'portrait')
        ?.widthMm
    : undefined
  useEffect(() => {
    const box = scrollBox.current
    if (!box || !framedSheetMm) {
      setFrameScale(1)
      return
    }
    const sheetPx = framedSheetMm * (96 / 25.4)
    const fit = () => {
      const style = getComputedStyle(box)
      const measure =
        box.clientWidth -
        Number.parseFloat(style.paddingInlineStart) -
        Number.parseFloat(style.paddingInlineEnd)
      setFrameScale(Math.min(1, measure / sheetPx))
    }
    fit()
    const observer = new ResizeObserver(fit)
    observer.observe(box)
    return () => observer.disconnect()
  }, [framedSheetMm])

  /* What each named font prints in is read beside the page, so the notice
     over the sheet only says that something is off and where to look. */
  const fontReferences = pagePreview?.page.fonts
  const fontViews = useMemo<CompanionView[]>(
    () =>
      fontReferences?.length
        ? [
            {
              id: FONTS_VIEW,
              label: 'Fonts',
              icon: <Type size={15} />,
              count: localFonts.unavailable.length,
              alert: localFonts.unavailable.length > 0,
              render: () => (
                <PageFonts
                  references={fontReferences}
                  localFonts={localFonts}
                  access={fontAccess}
                  onAsk={() => void askForLocalFonts()}
                />
              ),
            },
          ]
        : [],
    [fontReferences, localFonts, fontAccess],
  )
  useCompanionViews(fontViews)

  /* A page with no paper is a screen: its frame is the room on screen and the
     page scrolls inside it, which is what lets its own sticky elements stick.
     A sheet of paper grows to its own height and the window scrolls it. */
  const screen = !loaded.paper

  const measure = () => {
    heightObserver.current?.disconnect()
    const root = frame.current?.contentDocument?.documentElement
    if (!root) return
    if (!screen) {
      const update = () => setHeight(root.scrollHeight)
      update()
      heightObserver.current = new ResizeObserver(update)
      heightObserver.current.observe(root)
    }
    setLegacy(root.hasAttribute(`data-${PLATFORM_SLUG}-legacy`))
    setUnavailable(
      (root.getAttribute(`data-${PLATFORM_SLUG}-unavailable`) ?? '')
        .split(',')
        .filter(Boolean),
    )
  }

  const sheet = htmlPageSheetGeometry(
    loaded.paper,
    loaded.orientation ?? 'portrait',
  )
  const width = sheet?.widthMm
  const paperHeight = sheet?.heightMm
  const renderedHeight = Math.max(
    paperHeight ?? 0,
    height ? height / (96 / 25.4) : 0,
  )

  return (
    <section className="html-doc-view" aria-label={loaded.title}>
      <DownloadActions
        doc={doc}
        client={client}
        downloads={loaded.downloads}
        pagePreview={pagePreview}
        localFonts={localFonts}
        title={loaded.title}
      />
      {localFonts.unavailable.length > 0 && !fontsNoticeShut ? (
        <DocSlot kind="notice">
          <PageFontsNotice
            families={localFonts.unavailable.map((font) => font.family)}
            onShow={openCompanion ? () => openCompanion(FONTS_VIEW) : null}
            onDismiss={() => setFontsNoticeShut(true)}
          />
        </DocSlot>
      ) : null}
      <DocFacts
        facts={htmlPageFacts(loaded.facts, scripted)}
        onDemand={factsOnDemand}
      />
      {unavailable.length > 0 ? (
        <DocSlot kind="notice">
          <Notice>
            Could not fill {unavailable.join(', ')} for this preview.
          </Notice>
        </DocSlot>
      ) : null}
      {legacy ? (
        <DocSlot kind="notice">
          <Notice>
            This page was written for the older form and needs rewriting.
          </Notice>
        </DocSlot>
      ) : null}
      {ticketError && pagePreview ? (
        <DocSlot kind="notice">
          <Notice tone="error">{ticketError.message}</Notice>
        </DocSlot>
      ) : null}
      <div
        ref={scrollBox}
        className="html-doc-view__scroll"
        data-screen={screen ? '' : undefined}
      >
        {pagePreview && pageElementReady ? (
          createElement('segnavia-page', {
            ref: pageElement,
            bounded: screen,
            label: loaded.title,
            /* The sheet takes the frame's width up to its own, and Segnavia
               scales it down to fit a narrower frame, so a phone shows the
               whole sheet rather than a true-size one cut off at the edge. */
            style: width
              ? { width: '100%', maxWidth: `${width}mm` }
              : { width: '100%' },
          })
        ) : ticket ? (
          <iframe
            ref={frame}
            title={loaded.title}
            sandbox={htmlPageFrameSandbox(loaded.extra.kind)}
            referrerPolicy="no-referrer"
            src={ticket.url}
            onLoad={scripted ? undefined : measure}
            style={{
              width: width ? `${width}mm` : '100%',
              /* A framed page is zoomed down to the box's measure, as
                 Segnavia scales a sheet, since the page's own script lays it
                 out at the sheet's width. */
              ...(frameScale < 1 ? { zoom: frameScale } : {}),
              ...(screen
                ? {}
                : renderedHeight
                  ? width
                    ? { height: `${renderedHeight}mm` }
                    : { height: `${height}px` }
                  : {}),
            }}
          />
        ) : ticketError ? (
          <Notice tone="error">{ticketError.message}</Notice>
        ) : (
          <Notice>Preparing preview…</Notice>
        )}
      </div>
    </section>
  )
}
